import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import type { FieldTask } from '../../hooks/useFieldToday';
// completedAt is a full ISO instant, so the DAY it falls on is the tenant's
// (companies.time_zone) — formatInstantDay, not formatCalendarDate. The `[]` locale this
// replaces meant the reader's device chose the format; a month name does not
// need a device to agree with it.
import { formatInstantDay, formatInstantTime } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { resolveTaskRoute } from '../../lib/field-task-route';

interface TaskCardProps {
  task: FieldTask;
  onClick?: () => void;
}

type Vertical = 'trucking' | 'surface_rentals';

const verticalBadge: Record<Vertical, { label: string; cls: string }> = {
  trucking: { label: 'BOL', cls: 'bg-fieldiq-accent/15 text-fieldiq-accent border-fieldiq-accent/30' },
  surface_rentals: { label: 'Rental', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
};

// scheduledAt is an instant: shown on the tenant's wall clock, not the
// phone's. Absent or unreadable renders an em dash.
function formatTime(iso: string | undefined, timeZone: string | null) {
  return formatInstantTime(iso, timeZone, { hour: 'numeric', minute: '2-digit' }) ?? '—';
}

export default function TaskCard({ task, onClick }: TaskCardProps) {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const v = verticalBadge[task.vertical as Vertical] ?? verticalBadge.trucking;
  const inProgress = task.status === 'in_progress';
  const route = resolveTaskRoute(task.sourceRoute);

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    // `route` is null for the sourceRoute shapes no screen serves. Navigating
    // anyway lands on the tree's catch-all and puts the hand back on Today,
    // which is indistinguishable from a broken card — say so instead.
    if (!route) {
      toast.info(`${task.title} — no screen for this yet`);
      return;
    }
    navigate(route);
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left rounded-xl border bg-fieldiq-card p-4 transition-colors ${
        route ? 'hover:bg-fieldiq-card/30' : 'opacity-60'
      } ${
        inProgress ? 'border-l-4 border-l-fieldiq-accent border-fieldiq-border' : 'border-fieldiq-border'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <span
            className={`shrink-0 mt-0.5 inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${v.cls}`}
          >
            {v.label}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{task.title}</p>
            {task.subtitle && (
              <p className="mt-0.5 text-xs text-fieldiq-text truncate">{task.subtitle}</p>
            )}
            {task.meta && (
              <p className="mt-1 text-[11px] text-fieldiq-text/80 truncate">{task.meta}</p>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          {inProgress ? (
            <span className="text-[11px] font-semibold text-fieldiq-accent">In Progress</span>
          ) : task.scheduledAt ? (
            <span className="text-[11px] text-fieldiq-text">{formatTime(task.scheduledAt, timeZone)}</span>
          ) : task.completedAt ? (
            <span className="text-[11px] text-fieldiq-text">
              {formatInstantDay(task.completedAt, timeZone, { month: 'short', day: 'numeric' }) ?? '—'}
            </span>
          ) : null}
          {route && <ChevronRight className="ml-auto mt-1 h-4 w-4 text-fieldiq-text" />}
        </div>
      </div>
    </button>
  );
}
